import { isEmpty } from 'lodash';
import { CalculationType, CostLine, LineCalculationInfo } from 'sr-types/lib/opportunity/v1/graphql';
import { Measurement, Rate } from 'sr-types/lib/search/v1/graphql';
import { extractAttributeNumericValue } from '../../../pricing/PricingUtils';
import { isMoney } from '../../utils/money';
import { NumberParser } from '../../utils/numbers';
import PricingLine from './PricingLine';
import { CostCalculator } from './useCalculator';

export const CALC_TYPE_TO_UNIT = {
    ph: 'hour',
    pd: 'day',
    pw: 'week',
    pm: 'month',
    pq: 'quarter',
    py: 'year',
    'ph(sqft)': 'hour',
    'pd(sqft)': 'day',
    'pw(sqft)': 'week',
    'pm(sqft)': 'month',
    'py(sqft)': 'year',
    'ph(sqm)': 'hour',
    'pd(sqm)': 'day',
    'pw(sqm)': 'week',
    'pm(sqm)': 'month'
};

export const CALC_TYPE_TO_ATTRIBUTE = {
    'ph(sqft)': 'area(sqFt)',
    'pd(sqft)': 'area(sqFt)',
    'pw(sqft)': 'area(sqFt)',
    'pm(sqft)': 'area(sqFt)',
    'py(sqft)': 'area(sqFt)',
    'ph(sqm)': 'area(sqM)',
    'pd(sqm)': 'area(sqM)',
    'pw(sqm)': 'area(sqM)',
    'pm(sqm)': 'area(sqM)'
};

export const ATTRIBUTE_COSTLINE_VAR_MAP = {
    'area(sqFt)': 'area',
    'area(sqM)': 'area'
};

export enum DiscountCalculationTypesEnum {
    flt = 'flt',
    pct = 'pct'
}

export enum LineTypes {
    CostLine = 'CostLine',
    Discount = 'Discount'
}

export enum discountLevelTypes {
    Booking = 'Booking',
    Line = 'Line'
}

export const FlatCalcType = {
    code: DiscountCalculationTypesEnum.flt,
    assignTo: 'baseTotal',
    formula: 'itemPrice',
    label: 'Flat'
};

export const PercentCalcType = {
    code: DiscountCalculationTypesEnum.pct,
    assignTo: 'baseTotal',
    formula: '(itemPrice * itemPercent)/100',
    label: 'Percentage'
};

export const durationUnitToCalculationType = (durationUnit: string): string => {
    return Object.keys(CALC_TYPE_TO_UNIT).find((key) => CALC_TYPE_TO_UNIT[key] === durationUnit);
};

export const findCalculationTypeByCode = (calculationTypes: CalculationType[], typeCode: string): CalculationType => {
    const calculationType = calculationTypes.find((t: CalculationType) => t.code === typeCode);
    const calculationTypeObject = {
        code: calculationType?.code,
        assignTo: calculationType?.assignTo,
        formula: calculationType?.formula,
        label: calculationType?.label
    };
    return calculationTypeObject;
};

export const findCalculationTypeByDurationUnit = (calculationTypes: CalculationType[], durationUnit: string) => {
    const typeCode: string = durationUnitToCalculationType(durationUnit);
    return findCalculationTypeByCode(calculationTypes, typeCode);
};

// Atm the assumtion is only 1 row/cost line.
export const costLineToRows = (
    costLine: CostLine,
    calculator: CostCalculator,
    rate: Rate,
    dateTimeDuration = undefined,
    locale?: string
): PricingLine[] => {
    if (!isEmpty(costLine)) {
        const calculationType = rate
            ? rate.calculationType.code
            : costLine.lineCalculationInfo?.calculationInfo?.calculationType?.code;
        const row: PricingLine = {
            id: 1,
            lineType: costLine.lineCalculationInfo.lineType,
            identity: costLine?.identity?.id,
            preSave: costLine?.identity?.preSave,
            currency: costLine.lineCurrency,
            calculationType: calculationType,
            itemPrice: rate ? rate.itemPrice : costLine.lineCalculationInfo?.calculationInfo?.itemPrice,
            itemQuantity: costLine.lineCalculationInfo?.calculationInfo?.itemQuantity,
            leaseDuration: costLine.lineCalculationInfo?.calculationInfo?.leaseDuration?.value,
            baseTotal: costLine.lineCalculationInfo?.calculationInfo?.baseTotal,
            associatedFieldDefinitionReferences:
                costLine.lineCalculationInfo?.calculationInfo?.associatedFieldDefinitionReferences || [],
            attributes: costLine.lineCalculationInfo?.calculationInfo?.attributes || [],
            adjustments: costLine.adjustments,
            lineTotal: isMoney(costLine.lineTotal)
                ? costLine.lineTotal
                : isMoney(costLine.lineCalculationInfo?.calculationInfo?.baseTotal)
                  ? costLine.lineCalculationInfo?.calculationInfo?.baseTotal
                  : {},
            discounts: costLine.costSummary?.discount,
            discountedTotal:
                costLine.costSummary?.discountedTotal || costLine.lineCalculationInfo?.calculationInfo?.baseTotal || {}
        };

        const parser = new NumberParser(locale);
        costLine.lineCalculationInfo.calculationInfo.attributes?.map((attr) => {
            const costLineVar = ATTRIBUTE_COSTLINE_VAR_MAP[attr.name];
            if (costLineVar && attr.name === CALC_TYPE_TO_ATTRIBUTE[calculationType]) {
                row[costLineVar] = extractAttributeNumericValue(attr.value, parser);
            }
        });
        row.attributes = costLine.lineCalculationInfo.calculationInfo.attributes || [];

        if (dateTimeDuration) {
            row.calculationType = durationUnitToCalculationType(dateTimeDuration.unit);
            row.leaseDuration = dateTimeDuration.value;
        }
        return [calculator.calculate(row)];
    } else {
        return [];
    }
};

export const getEmptyCostLine = (
    defaultCurrency: string,
    calculationTypes: CalculationType[],
    dateTimeDuration: Measurement = undefined
): CostLine => {
    const calculationType = dateTimeDuration
        ? findCalculationTypeByDurationUnit(calculationTypes, dateTimeDuration.unit)
        : {
              code: undefined,
              assignTo: 'baseTotal',
              formula: undefined,
              label: undefined
          };
    const costLine: CostLine = {
        lineCurrency: defaultCurrency,
        lineTotal: {},
        lineCalculationInfo: {
            lineType: 'CostLine',
            calculationInfo: {
                autoCalculate: true,
                calculationType: calculationType,
                itemPrice: undefined,
                itemQuantity: 1,
                baseTotal: undefined,
                attributes: [],
                associatedFieldDefinitionReferences: [],
                leaseDuration: {
                    unit: dateTimeDuration ? dateTimeDuration.unit : undefined,
                    value: dateTimeDuration ? dateTimeDuration.value : undefined
                }
            }
        },
        adjustments: [],
        costSummary: {
            discountedTotal: {},
            discount: {},
            base: {},
            total: {},
            taxes: {}
        }
    };
    return costLine;
};

export const adjustmentsToRows = (
    adjustments: LineCalculationInfo[],
    discountSetting,
    costLineBaseTotal,
    lineCurrency,
    calculator
) => {
    let rows = [];
    if (!isEmpty(adjustments)) {
        adjustments.map((adjustment, idx) => {
            const row = {
                id: idx,
                itemPrice: isEmpty(adjustment.calculationInfo.calculationType.code)
                    ? ''
                    : adjustment.calculationInfo.calculationType.code == DiscountCalculationTypesEnum.pct
                      ? costLineBaseTotal
                      : adjustment.calculationInfo.itemPrice || discountSetting.amount,
                calculationType: adjustment.calculationInfo.calculationType.code,
                baseTotal: adjustment.calculationInfo.baseTotal,
                itemPercent: isEmpty(adjustment.calculationInfo.calculationType.code)
                    ? ''
                    : adjustment.calculationInfo.calculationType.code == DiscountCalculationTypesEnum.flt
                      ? adjustment.calculationInfo.itemPrice || discountSetting.amount
                      : adjustment.calculationInfo.itemPercent || discountSetting.percent,
                currency: lineCurrency,
                lineType: adjustment.adjustmentCategory,
                adjustmentName: discountSetting.name,
                adjustmentDescription: discountSetting.description
            };
            rows.push(calculator.calculate(row));
        });
        return rows;
    } else {
        return [];
    }
};
